import clsx from 'clsx';
import React from 'react';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { useDispatch } from 'react-redux';
import TextArea from '../../components/TextArea';
import Button from '../../components/Button';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import {
  Attachments,
  InvestigationQuestionWithCategory,
  InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
} from '@EHSNavigator/backend';
import RadioButtons from '../../components/RadioButtons';

interface InvestigationReportingStepTwoProps {
  otherClasses?: string;
  formHandler: any;
  handleNextStep: (steps: number) => void;
  attachments: Attachments[];
  questions: InvestigationQuestionWithCategory[];
}

const InvestigationReportingStepTwo: React.FC<
  InvestigationReportingStepTwoProps
> = ({ otherClasses, formHandler, handleNextStep, questions, attachments }) => {
  const investigationReportingStepTwoClasses = clsx(otherClasses);
  const dispatch = useDispatch();
  const sm = useButtonSizeSmall();

  return (
    <div
      className={investigationReportingStepTwoClasses}
      data-testid="investigation-reporting-step-two"
    >
      <div className="flex flex-row items-start gap-2.5">
        <button
          type="button"
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 w-fit cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex flex-col gap-1">
          <span className="text-brand-byzantine font-inter-variable text-xs leading-4 font-bold lg:hidden">
            INJURY/ILLNESS
          </span>
          <h4 className="text-neutral-tones-text font-inter-variable font-bold">
            Let’s Get to the Root Cause
          </h4>
          <p className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:mt-15">
            STEP 2 /{' '}
            {formHandler?.values?.files?.length > 0 || attachments?.length > 0
              ? 4
              : questions?.length > 0
                ? 5
                : 4}
          </p>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text mt-8 text-sm leading-4 font-medium lg:mt-9 lg:text-xl lg:leading-6">
        We kindly request that you respond to the following inquiries with the
        utmost accuracy and detail, as your insights are crucial for our ongoing
        injury investigation process.
      </p>

      <div className="mt-12 grid max-w-955 grid-cols-1 gap-9 md:grid-cols-2">
        {formHandler?.values?.files?.length === 0 &&
          attachments?.length === 0 &&
          formHandler.values.fiveWhysQuestionAnswer.map(
            (item: any, idx: number) => (
              <TextArea
                key={item.question}
                name={`fiveWhysQuestionAnswer.${idx}.answer`}
                label={
                  <>
                    {item.question}
                    <span className="text-brand-posh-pink">*</span>
                  </>
                }
                onChange={(e) => {
                  formHandler.setFieldValue(
                    `fiveWhysQuestionAnswer.${idx}.answer`,
                    e.target.value,
                  );
                }}
                placeholder="Type answer here…"
                value={item.answer}
                disabled={formHandler.isSubmitting}
                errorMessage={
                  formHandler.touched.fiveWhysQuestionAnswer &&
                  formHandler.touched.fiveWhysQuestionAnswer[idx]?.answer &&
                  formHandler.errors.fiveWhysQuestionAnswer &&
                  formHandler.errors.fiveWhysQuestionAnswer[idx]?.answer
                    ? formHandler.errors.fiveWhysQuestionAnswer[idx]?.answer
                    : ''
                }
                onBlur={() =>
                  formHandler.setFieldTouched(
                    `fiveWhysQuestionAnswer.${idx}.answer`,
                    true,
                  )
                }
                otherClasses={{
                  container: 'w-full gap-25',
                  label:
                    'text-sm leading-4 lg:text-xl lg:leading-6 font-bold text-neutral-tones-text',
                }}
              />
            ),
          )}

        <TextArea
          name={`narBeforeIncident`}
          label={
            <>
              What was employee doing just before the incident occurred?
              <span className="text-brand-posh-pink">*</span>
            </>
          }
          onChange={(e) => {
            formHandler.setFieldValue(`narBeforeIncident`, e.target.value);
          }}
          placeholder="Type answer here…"
          value={formHandler?.values?.narBeforeIncident}
          disabled={formHandler.isSubmitting}
          errorMessage={
            formHandler.touched.narBeforeIncident &&
            formHandler.errors.narBeforeIncident
              ? formHandler.errors.narBeforeIncident
              : ''
          }
          onBlur={() => formHandler.setFieldTouched(`narBeforeIncident`, true)}
          otherClasses={{
            container: 'w-full gap-25',
            label:
              'text-sm leading-4 lg:text-xl lg:leading-6 font-bold text-neutral-tones-text',
          }}
        />
        <TextArea
          name={`narWhatHappened`}
          label={
            <>
              What happened? Tell us how the injury or illness occurred?
              <span className="text-brand-posh-pink">*</span>
            </>
          }
          onChange={(e) => {
            formHandler.setFieldValue(`narWhatHappened`, e.target.value);
          }}
          placeholder="Type answer here…"
          value={formHandler?.values?.narWhatHappened}
          disabled={formHandler.isSubmitting}
          errorMessage={
            formHandler.touched.narWhatHappened &&
            formHandler.errors.narWhatHappened
              ? formHandler.errors.narWhatHappened
              : ''
          }
          onBlur={() => formHandler.setFieldTouched(`narWhatHappened`, true)}
          otherClasses={{
            container: 'w-full gap-25',
            label:
              'text-sm leading-4 lg:text-xl lg:leading-6 font-bold text-neutral-tones-text',
          }}
        />
        <TextArea
          name={`narObjectSubstance`}
          label={
            <>
              What object or substance directly harmed the employee?
              <span className="text-brand-posh-pink">*</span>
            </>
          }
          onChange={(e) => {
            formHandler.setFieldValue(`narObjectSubstance`, e.target.value);
          }}
          placeholder="Type answer here…"
          value={formHandler?.values?.narObjectSubstance}
          disabled={formHandler.isSubmitting}
          errorMessage={
            formHandler.touched.narObjectSubstance &&
            formHandler.errors.narObjectSubstance
              ? formHandler.errors.narObjectSubstance
              : ''
          }
          onBlur={() => formHandler.setFieldTouched(`narObjectSubstance`, true)}
          otherClasses={{
            container: 'w-full gap-25',
            label:
              'text-sm leading-4 lg:text-xl lg:leading-6 font-bold text-neutral-tones-text',
          }}
        />

        {(formHandler?.values?.classification ===
          InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum.Hospitalized ||
          formHandler?.values?.typeOfLoss ===
            InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.MedicalTreatmentBeyondFirstAid) && (
          <div className={clsx('flex flex-col gap-25')}>
            <h6 className="font-inter-variable text-neutral-tones-text -tracking-button text-sm leading-4 font-bold lg:text-xl lg:leading-6">
              Was employee treated in emergency room?
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <RadioButtons
              options={[
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
              ]}
              value={formHandler?.values?.treatmentFacilityType}
              onChange={(e) => {
                formHandler.setFieldValue(`treatmentFacilityType`, e);
              }}
              errorMessage={
                formHandler.touched.treatmentFacilityType &&
                formHandler.errors.treatmentFacilityType
                  ? formHandler.errors.treatmentFacilityType
                  : ''
              }
            />
          </div>
        )}
      </div>
      <div className="mt-9 flex items-center gap-8">
        <Button
          size={sm}
          type="button"
          otherClasses="w-full sm:w-185"
          variant="secondary"
          onClick={() => handleNextStep(1)}
        >
          <ChevronIcon className="h-6 w-6 rotate-90 [&>path]:fill-current" />
          Go Back
        </Button>
        <Button
          size={sm}
          type="button"
          otherClasses="w-full sm:w-185"
          onClick={() =>
            handleNextStep(
              formHandler?.values?.files?.length > 0 || attachments?.length > 0
                ? 4
                : questions.length > 0
                  ? 3
                  : 4,
            )
          }
        >
          Next
          <ChevronIcon className="h-6 w-6 -rotate-90 [&>path]:fill-current" />
        </Button>
      </div>
    </div>
  );
};

export default InvestigationReportingStepTwo;
